import { useCallback, useEffect, useState } from "react";
import { CheckCircle, Send } from "lucide-react";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { inputClass, selectClass } from "../components/FormField";
import { cn } from "@/shared/lib/cn";
import { answerSupportRequest, fetchSupportRequests, supportDate, type SupportRequest } from "../api/supportApi";

const STATUS_OPTIONS = [
  { value: "", label: "Tous les statuts" },
  { value: "open", label: "Ouvertes" },
  { value: "answered", label: "Répondues" },
  { value: "closed", label: "Clôturées" },
];

const ROLE_LABELS: Record<string, string> = { insurer: "Assurance", claims: "Sinistre", auditor: "Audit", admin: "Admin", super_admin: "Super admin" };

/** Admin : plaintes et requêtes envoyées depuis le bouton d'aide, avec réponse et clôture. */
export function SupportRequestsPage() {
  const [status, setStatus] = useState("open");
  const [requests, setRequests] = useState<SupportRequest[]>([]);
  const [replies, setReplies] = useState<Record<number, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<number | null>(null);

  const load = useCallback(() => {
    fetchSupportRequests(status)
      .then(setRequests)
      .catch((err) => setError(err instanceof Error ? err.message : "Chargement impossible."));
  }, [status]);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (id: number, close: boolean) => {
    setBusy(id);
    setError(null);
    try {
      await answerSupportRequest(id, { reply: replies[id] ?? "", close });
      setReplies((current) => ({ ...current, [id]: "" }));
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Enregistrement impossible.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <Breadcrumbs items={[{ label: "Plaintes et requêtes" }]} />
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-foreground">Plaintes et requêtes</h1>
          <p className="text-sm text-muted">Messages envoyés par les utilisateurs depuis le bouton d'aide.</p>
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className={`${selectClass} sm:max-w-xs`} aria-label="Statut">
          {STATUS_OPTIONS.map((option) => (
            <option key={option.value || "all"} value={option.value}>{option.label}</option>
          ))}
        </select>
      </div>
      {error ? <p className="mb-4 text-sm text-red-600">{error}</p> : null}

      {requests.length === 0 ? (
        <div className="theme-card p-8 text-center text-sm text-muted">Aucune plainte ni requête.</div>
      ) : (
        <ul className="flex flex-col gap-3">
          {requests.map((request) => (
            <li key={request.id} className="theme-card flex flex-col gap-3 p-5">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <p className="font-semibold text-foreground">{request.category_label} · {request.subject}</p>
                  <p className="text-xs text-muted-foreground">
                    {request.author} ({ROLE_LABELS[request.author_role] ?? request.author_role}) · {supportDate(request.created_at)}
                  </p>
                </div>
                <span className={cn("rounded-full px-2.5 py-0.5 text-xs font-medium", request.status === "open" ? "bg-amber-50 text-amber-700" : request.status === "answered" ? "bg-sky-50 text-sky-700" : "bg-emerald-50 text-emerald-700")}>
                  {request.status_label}
                </span>
              </div>
              <p className="whitespace-pre-line text-sm text-foreground">{request.message}</p>
              {request.reply ? (
                <p className="rounded-md bg-card-muted px-3 py-2 text-sm text-foreground">
                  <span className="font-medium">Réponse{request.replied_by ? ` de ${request.replied_by}` : ""} :</span> {request.reply}
                </p>
              ) : null}
              {request.status !== "closed" ? (
                <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                  <textarea
                    value={replies[request.id] ?? ""}
                    onChange={(e) => setReplies((current) => ({ ...current, [request.id]: e.target.value }))}
                    rows={2}
                    placeholder="Votre réponse…"
                    aria-label={`Réponse à ${request.subject}`}
                    className={`${inputClass} h-auto flex-1 py-2`}
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      disabled={busy === request.id || !(replies[request.id] ?? "").trim()}
                      onClick={() => void act(request.id, false)}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-white hover:bg-accent hover:text-accent-foreground disabled:opacity-60"
                    >
                      <Send size={14} aria-hidden="true" />
                      Répondre
                    </button>
                    <button
                      type="button"
                      disabled={busy === request.id}
                      onClick={() => void act(request.id, true)}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm font-medium text-foreground hover:border-accent hover:bg-accent/20 disabled:opacity-60"
                    >
                      <CheckCircle size={14} aria-hidden="true" />
                      Clôturer
                    </button>
                  </div>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
